import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { ArrowLeft, Clock, Calendar } from "lucide-react";
import { useI18n } from "@/lib/i18nContext";
import { api } from "@/lib/api";
import { resolveMediaUrl } from "@/lib/media";
import Seo, { blogPostingSchema } from "@/components/Seo";

const LOCALE_FOR_LANG = { de: "de-DE", en: "en-GB", fr: "fr-FR" };

export default function BlogPostPage() {
  const { t, tl, lang, pathWithLang } = useI18n();
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setPost(null);
    setNotFound(false);
    api
      .get(`/blog/${slug}`)
      .then((r) => setPost(r.data))
      .catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) {
    return (
      <div className="max-w-[800px] mx-auto px-6 py-32 text-center" data-testid="post-not-found">
        <h1 className="font-display text-4xl mb-4">{t("blog.postNotFound")}</h1>
        <Link to={pathWithLang("/blog")} className="link-gold">{t("common.backToBlog")}</Link>
      </div>
    );
  }

  if (!post) return null;

  const dateLocale = LOCALE_FOR_LANG[lang] || "de-DE";
  const tr = post.translations?.[lang];
  const title = (tr && tr.title) || tl(post.title) || post.title;
  const excerpt = (tr && tr.excerpt) || tl(post.excerpt) || post.excerpt;
  const content = (tr && tr.content) || tl(post.content) || post.content;

  return (
    <article data-testid="blog-post-page" className="max-w-[860px] mx-auto px-6 lg:px-8 pt-20 lg:pt-28 pb-24">
      <Seo
        title={title}
        description={excerpt}
        type="article"
        image={resolveMediaUrl(post.cover_image)}
        article={{
          publishedAt: post.published_at || post.created_at,
          modifiedAt: post.updated_at || post.published_at || post.created_at,
          author: "Karim Benna",
          tags: post.tags,
        }}
        jsonLd={blogPostingSchema({
          title,
          description: excerpt,
          slug: post.slug,
          publishedAt: post.published_at || post.created_at,
          modifiedAt: post.updated_at,
          image: resolveMediaUrl(post.cover_image),
          lang,
          tags: post.tags,
        })}
      />
      <Link
        to={pathWithLang("/blog")}
        data-testid="back-to-blog"
        className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-mono text-[var(--text-2)] hover:text-[var(--gold)] transition-colors mb-12"
      >
        <ArrowLeft size={14} /> {t("common.backToBlog")}
      </Link>

      <div className="flex flex-wrap gap-3 mb-8">
        {post.tags?.map((tg) => (
          <span key={tg} className="tag-pill">{tg}</span>
        ))}
      </div>

      <h1 className="font-display text-[clamp(2rem,5vw,4rem)] leading-[1.05] tracking-[-0.02em] text-[var(--text-0)]">
        {title}
      </h1>

      <div className="flex flex-wrap items-center gap-6 mt-8 text-xs uppercase tracking-[0.2em] font-mono text-[var(--text-2)] pb-8 border-b hairline">
        <span className="flex items-center gap-2">
          <Calendar size={14} />
          {new Date(post.created_at).toLocaleDateString(dateLocale, {
            day: "2-digit", month: "long", year: "numeric",
          })}
        </span>
        <span className="flex items-center gap-2">
          <Clock size={14} />
          {post.reading_minutes} {t("blog.readingTime")}
        </span>
        <span>{post.author}</span>
      </div>

      {post.cover_image && (
        <div className="my-12 border hairline overflow-hidden">
          <img src={resolveMediaUrl(post.cover_image)} alt={title} className="w-full h-auto" />
        </div>
      )}

      {excerpt && (
        <p className="font-display italic text-2xl text-[var(--text-1)] leading-relaxed my-10">
          {excerpt}
        </p>
      )}

      <div className="prose-karim">
        <ReactMarkdown remarkPlugins={[remarkGfm]}>{content}</ReactMarkdown>
      </div>

      <div className="mt-20 border-t hairline pt-12 flex flex-col md:flex-row items-start md:items-center gap-6 justify-between">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[var(--text-2)]">
            {t("blog.inspired")}
          </div>
          <div className="font-display text-2xl text-[var(--text-0)] mt-1">
            {t("blog.letsTalk")}
          </div>
        </div>
        <Link to={pathWithLang("/kontakt")} data-testid="post-book-btn" className="btn-primary">
          {t("common.bookCall")}
        </Link>
      </div>
    </article>
  );
}
